import React, { useCallback, useEffect, useState } from 'react';
import api from '../../api/axios';

function statusClasses(state) {
  if (state === 'paused') return 'border-red-200 bg-red-50 text-red-800';
  if (state === 'monitoring') return 'border-amber-200 bg-amber-50 text-amber-800';
  if (state === 'recovered') return 'border-blue-200 bg-blue-50 text-blue-800';
  return 'border-green-200 bg-green-50 text-green-800';
}

function dateLabel(value) {
  return value ? new Date(value).toLocaleString() : '—';
}

export default function LinkedInPlatformHealthPanel() {
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setError('');
      const response = await api.get('/operations/platform-incidents?lookbackMinutes=30&limit=20&eventLimit=8');
      setData(response.data?.linkedin || null);
      setForbidden(false);
    } catch (requestError) {
      if (requestError.response?.status === 403) {
        setForbidden(true);
        setData(null);
        return;
      }
      setError(requestError.response?.data?.message || requestError.response?.data?.error || requestError.message);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (forbidden) return null;
  if (!data && !error) return <section className="rounded-xl border bg-white p-5 text-sm text-gray-500">LinkedIn platformstatus laden…</section>;

  const status = data?.status || { state: 'healthy', label: 'LinkedIn gezond', detail: '' };
  const counts = data?.health?.counts || {};
  const events = data?.events || [];

  return (
    <section className="rounded-xl border bg-white overflow-hidden">
      <div className="border-b px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-semibold text-gray-900">LinkedIn platform health</h2>
            <p className="text-sm text-gray-500">Platformbrede selector-, sessie- en canary-signalen. Alleen operationele aggregaten worden getoond.</p>
          </div>
          <button onClick={load} className="rounded-lg border px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Vernieuwen</button>
        </div>
      </div>

      {error ? (
        <div className="p-5 text-sm text-red-700">Platformstatus kon niet worden geladen: {error}</div>
      ) : (
        <div className="p-5 space-y-5">
          <div className={`rounded-lg border px-4 py-3 ${statusClasses(status.state)}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <strong>{status.label}</strong>
              <span className="text-xs font-medium">Circuit breaker: {data?.circuit?.blocked ? 'actief' : 'vrij'}</span>
            </div>
            <p className="mt-1 text-sm opacity-90">{status.detail}</p>
            {data?.circuit?.blocked && data?.circuit?.retryAt && (
              <p className="mt-1 text-xs">Volgende task-herbeoordeling: {dateLabel(data.circuit.retryAt)}</p>
            )}
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border p-3"><span className="text-xs uppercase tracking-wide text-gray-500">Open</span><strong className="mt-1 block text-xl text-red-700">{counts.open || 0}</strong></div>
            <div className="rounded-lg border p-3"><span className="text-xs uppercase tracking-wide text-gray-500">Monitoring</span><strong className="mt-1 block text-xl text-amber-700">{counts.candidate || 0}</strong></div>
            <div className="rounded-lg border p-3"><span className="text-xs uppercase tracking-wide text-gray-500">Geobserveerd</span><strong className="mt-1 block text-xl text-gray-700">{counts.observed || 0}</strong></div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-900">Incident lifecycle</h3>
              <span className="text-xs text-gray-400">Bijgewerkt {dateLabel(data?.generatedAt)}</span>
            </div>
            <div className="divide-y rounded-lg border">
              {events.length === 0 && <div className="p-3 text-sm text-gray-500">Geen recente platformincidenten.</div>}
              {events.map((event, index) => (
                <div key={`${event.signature || 'event'}-${event.createdAt || index}`} className="flex flex-col gap-1 p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <span className={`text-sm font-medium ${event.eventType === 'incident_resolved' ? 'text-green-700' : 'text-red-700'}`}>
                      {event.eventType === 'incident_resolved' ? 'Hersteld' : 'Incident geopend'}
                    </span>
                    <p className="text-xs text-gray-500">{event.errorCode} · {event.pageType} · {event.distinctAccounts} accounts · {event.failureCount} signalen</p>
                  </div>
                  <span className="text-xs text-gray-400">{dateLabel(event.resolvedAt || event.createdAt)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
